/**
 * CustomerNotes — Admin page for viewing and adding customer notes.
 * Customer-centric with optional subscription context.
 */

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Paper, Chip, Alert, Avatar, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import billingApi from '../../services/billingApi';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const NOTE_TYPES = [
  { value: 'general', label: 'General', color: 'default' },
  { value: 'billing', label: 'Billing', color: 'primary' },
  { value: 'support', label: 'Support', color: 'info' },
  { value: 'internal', label: 'Internal', color: 'warning' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CustomerNotes() {
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [customerFilter, setCustomerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    customer_id: '', subscription_id: '', note: '', note_type: 'general',
  });

  const { confirm, confirmState, closeConfirm } = useConfirm();

  useEffect(() => { loadNotes(); }, [typeFilter]);

  async function loadNotes() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (customerFilter) params.set('customer_id', customerFilter);
      if (typeFilter) params.set('note_type', typeFilter);
      const data = await billingApi.get(`/api/admin/notes?${params}`);
      setNotes(data.notes || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      if (!form.customer_id || !form.note) {
        setError('Customer ID and note text are required');
        return;
      }
      await billingApi.post('/api/admin/notes', {
        customer_id: form.customer_id,
        subscription_id: form.subscription_id || null,
        note: form.note,
        note_type: form.note_type,
      });
      setDialog(false);
      setForm({ customer_id: '', subscription_id: '', note: '', note_type: 'general' });
      setSuccess('Note added');
      loadNotes();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    confirm({
      title: 'Delete Note',
      message: 'Delete this note?',
      destructive: true,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await billingApi.del(`/api/admin/notes/${id}`);
          loadNotes();
        } catch (err) {
          setError(err.message);
        }
      },
    });
  }

  function handleSearch(e) {
    e.preventDefault();
    loadNotes();
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Customer Notes
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog(true)}
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
          Add Note
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }} component="form" onSubmit={handleSearch}>
        <TextField
          size="small" label="Customer ID" value={customerFilter}
          onChange={e => setCustomerFilter(e.target.value)}
          sx={{ minWidth: 300 }}
          placeholder="Paste customer UUID to filter"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {NOTE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button type="submit" variant="outlined" size="small">Search</Button>
      </Box>

      {loading ? (
        <AdminPageSkeleton bare rows={5} />
      ) : notes.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No notes found
        </Paper>
      ) : (
        <Box>
          {notes.map(note => (
            <Paper key={note.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={note.note_type}
                      color={NOTE_TYPES.find(t => t.value === note.note_type)?.color || 'default'}
                      size="small" variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(note.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {note.author_name || 'System'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {note.note}
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.6rem' }}>
                      Customer: {note.customer_email || note.customer_id}
                    </Typography>
                    {note.subscription_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.6rem' }}>
                        Sub: {note.subscription_id.substring(0, 12)}...
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => handleDelete(note.id)} sx={{ color: 'text.disabled' }} aria-label="Delete note">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Create Note Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Customer Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Customer ID *" value={form.customer_id}
            onChange={e => setForm({ ...form, customer_id: e.target.value })}
            helperText="UUID of the customer record"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label="Subscription ID (optional)" value={form.subscription_id}
            onChange={e => setForm({ ...form, subscription_id: e.target.value })}
            helperText="Link this note to a specific subscription"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select value={form.note_type} label="Type"
              onChange={e => setForm({ ...form, note_type: e.target.value })}>
              {NOTE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth multiline rows={4} label="Note *" value={form.note}
            onChange={e => setForm({ ...form, note: e.target.value })}
            placeholder="Enter note..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmState} onClose={closeConfirm} />
    </Container>
  );
}
